// The one stacking order the runtime's overlays share.
//
// Every full-screen layer used to carry its own magic number, and the ladder
// only held together while nobody opened one FROM another. The process trail
// broke it: it sat at 10001, above the record editor at 9430, so choosing Edit
// or Assign inside the trail opened a window BEHIND the trail — visible, but
// unreachable.
//
// The rule that fixes it is a rule about origin, not about importance: **a
// layer opened from inside another layer stacks above it.** The trail window is
// opened from a list row or a diagram, so it sits below the editor; the editor
// is opened from the list or from the trail, so it sits above both; a lookup or
// a child-row editor is opened from inside the editor, so it sits above that.
// A menu drawn inside a window is the exception that proves the rule — it
// belongs to its window and must clear it, but it is always closed by the time
// the window it opens appears.
//
// Numbers are spaced so a new layer can be slotted in without renumbering.

import { createContext } from 'react'

/** The Run view's sticky top bar. Everything below is measured against it. */
export const Z_TOP_BAR = 9000

/** A view that replaces the page inside the runtime shell (a full-screen list,
 *  an embedded capture preview). */
export const Z_FULLSCREEN_VIEW = 9410

/** The process-trail window. Opened FROM a list row or a diagram, and every
 *  record action it offers opens a window of its own on top of it. */
export const Z_TRAIL_WINDOW = 9415

/** A transaction opened in a drawer by an 'open' action button. Below the
 *  record editor on purpose: the record it opens appears in the ordinary
 *  record editor window, which must clear the drawer it came from — and the
 *  drawer closes with that window, so it never strands behind another one. */
export const Z_OPEN_DRAWER = 9425

/** The record editor, the worklist, the approvals inbox — the window a record
 *  opens in, from a list or from the trail. */
export const Z_RECORD_EDITOR = 9430

/** A process / dashboard / visual opened in a drawer by an 'open' action
 *  button — and a transaction opened in VIEW mode, whose record is drawn
 *  inside the drawer rather than in a window of its own. It may be opened from
 *  inside the record editor, so it clears it; its own pickers (criteria
 *  look-ups) sit above it at Z_LOOKUP. */
export const Z_OPEN_DRAWER_OVER_EDITOR = 9435

/** A lookup picker, opened from a field inside the record editor. */
export const Z_LOOKUP = 9440

/** A child-row editor, opened from a detail grid inside the record editor. */
export const Z_CHILD_EDITOR = 9450

/** A kebab menu drawn on a page, and the assignment dialog it can open. */
export const Z_ROW_MENU = 9460

/** A kebab menu drawn INSIDE the trail window: above its own window, below the
 *  windows its items open (which is safe — picking an item closes the menu). */
export const Z_MENU_OVER_TRAIL = 9470

/** Small record windows opened from a kebab: audit trail, aliases. */
export const Z_RECORD_DIALOG = 10001

/** The Export window (opened from the list, the selection bar or an open
 *  record's ⋮) and, opened from inside it, the export template manager. */
export const Z_EXPORT_WINDOW = Z_RECORD_DIALOG
export const Z_EXPORT_TEMPLATES = Z_RECORD_DIALOG + 5

/** A confirmation the user must answer before anything else proceeds — the
 *  warnings gate, the action-confirm gate, discard-changes. */
export const Z_CONFIRM = 12000

/** How far a HOST window lifts the layers opened inside it. A host that is
 *  itself a full-screen overlay sitting above this ladder (the Assistant's
 *  import preview, above the Assistant panel) provides it, so a menu portalled
 *  out of it to <body> clears the window it was opened in — the same rule as
 *  above, applied to a window the runtime does not own. 0 everywhere else. */
export const LayerLiftContext = createContext(0)
